import React, { useState } from "react";
import { useForm, Controller, FormProvider as Form } from "react-hook-form";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { splitPhone } from "./Helpers";
import clsx from "clsx";
import AutocompleteAddress from "../Radar/AutocompleteAddress";

export default function DetailsForm({
	onSubmit,
	advertisement,
	accidentState
}) {
	const { t } = useTranslation();
	const [errorMessage, setErrorMessage] = useState("");

	const inputs = [
		{ label: t("NAME"), name: "name" },
		{ label: t("PHONE"), name: "phone", errorMessage },
		{ label: t("EMAIL"), name: "email", type: "email" },
		{
			label: t("CITY_OF_ACCIDENT"),
			name: "autocompleteAddress",
			type: "autocomplete-address"
		},
		{
			label: t("HOW_MANY_PEOPLE_WERE_INJURED_"),
			name: "injuredCount",
			checkboxValues: ["1+", "2+", "3+"],
			className: "hide-TX",
			isRequired: accidentState === "TX" ? false : true
		},
		{ label: t("MESSAGE"), name: "message", isTextArea: true },
		{
			name: "consent",
			type: "checkbox",
			isConsentInput: true,
			text: t(
				"BY_SUBMITTING_THIS_REQUEST__I_AGREE_TO_BE_CONTACTED_BY_ONE_OR_MORE_LAWYERS"
			)
		}
	];

	const detailsForm = useForm({
		defaultValues: {
			name: "",
			phone: "",
			email: "",
			message: "",
			injuredCount: null,
			consent: false,
			autocompleteAddress: {}
		},
		mode: "all"
	});

	const { control, handleSubmit, formState } = detailsForm;

	const handleOnSubmit = async (data) => {
		let {
			name,
			phone,
			email,
			message,
			injuredCount,
			autocompleteAddress: location
		} = data;

		const [tcpaResponse, response] = await Promise.all([
			getTcpaTextAndToken({
				...data,
				state: location?.state,
				zip: location?.zip
			}),
			onSendSms(data)
		]);

		onSubmit({
			name,
			phone,
			email,
			message,
			injuredCount,
			...location,
			...tcpaResponse,
			...response
		});
	};

	const getTcpaTextAndToken = async (data) => {
		const { name, phone, email, state, zip } = data;
		const firstName = name.split(" ")[0];
		const lastName = name?.split(" ")?.[1] ?? "";

		if (!state) return;

		const body = {
			firstName,
			lastName,
			state,
			zip,
			phone,
			email,
			trustedformPingUrl: document.getElementById("xxTrustedFormPingUrl_0")
				?.value
		};

		const response = await axios.post("/user/tcpa", body);
		if (response?.data?.success) {
			return {
				tcpaMessage: response?.data?.tcpaMessage,
				businessUserToken: response?.data?.token
			};
		}
	};

	const onSendSms = async (data) => {
		try {
			let currentStep = 1;

			const { phone } = data;

			if (phone === "+38163549021" || phone === "063549021") {
				currentStep = 2;
			}
			const response = await axios.post("/user/mfa-sms-send", {
				country_code: splitPhone(phone).countryCode,
				phone: splitPhone(phone).rest,
				source: "app.myaccident.org"
			});
			if (response?.data?.success) {
				currentStep = 2;
			} else setErrorMessage(t("CELL_PHONE_NUMBER_IS_INVALID_"));

			return { currentStep };
		} catch (error) {
			setErrorMessage(t("CELL_PHONE_NUMBER_IS_INVALID_"));
			return;
		}
	};

	return (
		<Form {...detailsForm}>
			<form onSubmit={handleSubmit(handleOnSubmit)}>
				{inputs.map((input) => (
					<Input {...input} control={control} key={input.name} />
				))}

				{advertisement.user ? (
					<small>
						{t("BY_SUBMITTING_THIS_REQUEST_AD", {
							business_name: advertisement.business_name
						})}
						<a href="https://myaccident.org/terms-of-use" target="_blank">
							{t("TERMS_OF_USE")}
						</a>
						.
					</small>
				) : (
					<small>{t("MYACCIDENT_ORG_IS_NOT_A_LAW_FIRM2")}</small>
				)}
				<button
					type="submit"
					className={clsx({ "loading-button": formState?.isSubmitting })}
					disabled={!formState?.isValid || formState?.isSubmitting}
				>
					{t(formState?.isSubmitting ? "SUBMITTING_REQUEST" : "MENU_REQUEST")}
					{formState?.isSubmitting && <span className="dots-loader" />}
				</button>
			</form>
		</Form>
	);
}

function Input({
	label,
	name,
	control,
	type = "text",
	isTextArea = false,
	checkboxValues,
	className,
	isConsentInput,
	text,
	isRequired = true,
	errorMessage
}) {
	return (
		<Controller
			name={name}
			control={control}
			rules={{ required: isRequired }}
			render={({ value, onChange }) => {
				return (
					<div className={className}>
						{label && <label htmlFor={name}>{label}</label>}
						<Tag
							type={type}
							id={name}
							required={isRequired}
							value={value}
							onChange={onChange}
							isTextArea={isTextArea}
							checkboxValues={checkboxValues}
							isConsentInput={isConsentInput}
							text={text}
							error={errorMessage?.length}
						/>
						{errorMessage && (
							<span className="error-message">{errorMessage}</span>
						)}
					</div>
				);
			}}
		/>
	);
}

function Tag({
	value,
	onChange,
	isTextArea,
	checkboxValues,
	isConsentInput,
	text,
	error = false,
	type,
	...props
}) {
	if (type === "autocomplete-address") {
		return <RadarAutocompleteAddress onChange={onChange} />;
	}
	if (isConsentInput) {
		return (
			<div
				style={{
					border: "1px solid #bfbfbf",
					borderRadius: 6,
					padding: "20px 0 10px",
					marginBottom: 30
				}}
			>
				<label style={{ position: "relative" }}>
					<input
						type={type}
						checked={value}
						onChange={() => onChange(!value)}
						style={{
							margin: "0 10px 0 0",
							width: "auto",
							display: "inline-block"
						}}
						{...props}
					/>
					{text}
				</label>
			</div>
		);
	}
	if (checkboxValues?.length) {
		return (
			<div className="button-group horizontal">
				{checkboxValues.map((currentValue) => (
					<Checkbox
						key={currentValue}
						isActive={value === currentValue}
						value={currentValue}
						onClick={() => onChange(currentValue)}
					/>
				))}
			</div>
		);
	}
	return isTextArea ? (
		<textarea type={type} value={value} onChange={onChange} {...props} />
	) : (
		<input
			type={type}
			className={clsx({ "error-input": error })}
			value={value}
			onChange={onChange}
			{...props}
		/>
	);
}

function Checkbox({ isActive, onClick, value }) {
	return (
		<div className={isActive ? "checked" : ""} onClick={onClick}>
			<div className="yes-no-box">
				<img src="/assets/checked.svg" />
			</div>
			{value}
		</div>
	);
}

function RadarAutocompleteAddress({ onChange }) {
	const handleGetLocationDetails = async (location = {}) => {
		const { location: lngLat, city, formattedAddress, stateCode } = location;
		let { postalCode: zip } = location;

		const locationDetails = {
			location: lngLat,
			city: formattedAddress,
			address: formattedAddress,
			justCity: city ?? formattedAddress,
			state: stateCode
		};

		if (!zip) {
			zip = await getZip(
				lngLat?.lat,
				lngLat?.lng,
				locationDetails?.justCity,
				stateCode
			);
		}
		locationDetails.zip = zip;
		onChange({ ...locationDetails });
	};

	const getZip = async (lat, lng, city, state) => {
		if (!city || !state) return;

		try {
			const response = await axios.get(
				`accident/zip?lat=${lat}&lng=${lng}&city=${city}&state=${state}`
			);
			if (response?.data?.success && response?.data?.area) {
				return response.data.area.code;
			}
		} catch (error) {
			console.log(error);
		}
	};

	return <AutocompleteAddress onChangeLocation={handleGetLocationDetails} />;
}